import {
  CellClassFunc,
  CellRendererSelectorFunc,
  CellStyleFunc,
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  IFilterDef,
  IRowNode,
  ISetFilterParams,
  KeyCreatorParams,
  ValueFormatterFunc,
  ValueGetterFunc,
} from "@ag-grid-community/core";
import { css, toCSSVar } from "@chakra-ui/react";
import clsx from "clsx";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableColumn } from "gen/model/genericTableColumn";
import { GenericTableColumnSetFilterValue } from "gen/model/genericTableColumnSetFilterValue";
import { GenericTableData } from "gen/model/genericTableData";
import { GenericTableRow } from "gen/model/genericTableRow";
import { GenericTableRowCells } from "gen/model/genericTableRowCells";
import i18n from "i18next";
import { first } from "lodash-es";
import { useMemo } from "react";
import {
  isCellRowSpanned,
  isCellRowSpannedToBottom,
  isFirstVisibleColumn,
  tryGetAgClientSideRowModel,
} from "shared/components/table/ag/AgGridUtils";
import { DataTableContext } from "shared/components/table/data/DataTableContext";
import { DataTableProps } from "shared/components/table/data/DataTableProps";
import {
  GenericTableAgGridReactProps,
  isPaginationRow,
  isTotalRow,
} from "shared/components/table/data/DataTableTypes";
import { DataTableCellRenderer } from "shared/components/table/data/internal/DataTableCellRenderer";
import {
  COLUMN_ID_ACTIONS,
  COLUMN_ID_AUTO_COLUMN,
} from "shared/components/table/data/internal/DataTableConstants";
import { buildDataTableColumnContext } from "shared/components/table/data/internal/DataTableContextBuilders";
import { DataTableHeaderCellRenderer } from "shared/components/table/data/internal/DataTableHeaderCellRenderer";
import { dataTableHeaderClass } from "shared/components/table/data/internal/DataTableHeaderClass";
import { DataTableHeaderComponent } from "shared/components/table/data/internal/DataTableHeaderComponent";
import { DataTableHeaderGroupComponent } from "shared/components/table/data/internal/DataTableHeaderGroupComponent";
import {
  formattedValueForCell,
  getCellForRowGroup,
  getFirstInitialRowGroupColumn,
  isCellSkipped,
  isGenericTableCellTextOnly,
  rowSpan,
  valueForCellFilter,
  valueForCellKeyCreator,
} from "shared/components/table/data/internal/DataTableInternalUtils";
import { DataTableRowActions } from "shared/components/table/data/internal/DataTableRowActions";
import { maybernCellErrorClass } from "shared/components/table/maybern/MaybernTableConstants";
import { isMaybernTableColumnLastInGroup } from "shared/components/table/maybern/MaybernTableUtils";
import { theme } from "theme";
import { match, P } from "ts-pattern";

const maybernChakraTheme = toCSSVar(theme);

type ColOrGroupDef =
  | ColDef<GenericTableRow, GenericTableCell>
  | ColGroupDef<GenericTableRow>;

export const useDataTableColumnAgProps = (
  props: DataTableProps,
  tableContext: DataTableContext,
): GenericTableAgGridReactProps => {
  const { isEmpty, renderRowActions } = tableContext;
  const { data: tableData, extraColumns } = props;
  return useMemo(() => {
    if (isEmpty) {
      // Don't show any columns if there's no data to prevent flicker
      // before empty state is shown.
      return {};
    }
    const rootColumns = translateColumns(tableData, tableContext);

    if (renderRowActions) {
      rootColumns.push(ACTIONS_COL_DEF);
    }

    if (extraColumns) {
      rootColumns.push(...extraColumns);
    }

    return { columnDefs: rootColumns };
  }, [isEmpty, tableData, tableContext, renderRowActions, extraColumns]);
};

const keyCreator = (
  params: KeyCreatorParams<GenericTableRow, GenericTableCell>,
) => valueForCellKeyCreator(params.data.cells[params.column.getColId()]);

const cellStyle: CellStyleFunc<GenericTableRow, GenericTableCell> = (props) => {
  const tableContext: DataTableContext = props.context;
  const columnContext = buildDataTableColumnContext(tableContext, props.column);
  const row = props.node;
  const cell = props.value;
  if (!cell) {
    return undefined;
  }
  const styles = tableContext.stylesForCell?.({
    type: "data",
    tableContext,
    columnContext,
    row,
    cell,
  });
  return styles ? css(styles)(maybernChakraTheme) : undefined;
};

const valueFormatter: ValueFormatterFunc<GenericTableRow, GenericTableCell> = (
  params,
) => formattedValueForCell(params.context, params.value) || "";

const cellRendererSelector: CellRendererSelectorFunc<
  GenericTableRow,
  GenericTableCell
> = (params) => {
  const {
    hasExpandableRows,
    getActionsForCell,
    getActionsForColumn,
    getLinkInfoForCell,
    getLinkInfoForRow,
    getRowSpanKeyForCell,
    onButtonClick,
    renderCellValue,
    renderRowActions,
    stylesForCell,
  }: DataTableContext = params.context;
  const showGroupArrow =
    isFirstVisibleColumn(params.api, params.column) && hasExpandableRows;

  // for pivoting
  if (params.column?.getColId() === COLUMN_ID_AUTO_COLUMN) {
    return undefined;
  }

  if (showGroupArrow) {
    return {
      component: "agGroupCellRenderer",
    };
  }
  // don't use custom renderer if not needed (for performance)
  if (
    params.value &&
    isGenericTableCellTextOnly(params.value) &&
    !getActionsForCell &&
    !getActionsForColumn &&
    !getLinkInfoForCell &&
    !getLinkInfoForRow &&
    !getRowSpanKeyForCell &&
    !onButtonClick &&
    !renderCellValue &&
    !renderRowActions &&
    stylesForCell &&
    params.node.allChildrenCount === 0
  ) {
    return undefined;
  }
  return {
    component: DataTableCellRenderer,
  };
};

const cellClass: CellClassFunc<GenericTableRow, GenericTableCell> = (
  params,
) => {
  const tableContext: DataTableContext = params.context;
  const { column, inferredData, rootColumn } = buildDataTableColumnContext(
    tableContext,
    params.column,
  );
  const cell = params.data?.cells[column.id];
  const cellValueType = cell?.value?.type ?? inferredData?.valueType;
  return clsx(tableContext.headerOrCellClass?.(params), {
    [`mb-cell-value-type-${cellValueType}`]: !!cellValueType,
    "mb-cell-last-in-group": isMaybernTableColumnLastInGroup(params),
    "mb-column-has-form-field": inferredData?.hasFormField,
    "mb-cell-is-header": !!(column.is_header || rootColumn?.is_header),
    [maybernCellErrorClass]: !!(params.value as GenericTableCell | undefined)
      ?.error,
    "mb-cell-is-row-spanned": isCellRowSpanned(params),
    "mb-cell-is-row-spanned-to-bottom": isCellRowSpannedToBottom(params),
    "mb-cell-is-skipped":
      cell &&
      isCellSkipped({
        api: params.api,
        tableContext,
        columnContext: buildDataTableColumnContext(tableContext, params.column),
        row: params.node,
        cell,
      }),
  }).split(" ");
};

const valueGetter: ValueGetterFunc<GenericTableRow, GenericTableCell> = ({
  api,
  data,
  node,
  column,
  context,
  ...params
}) => {
  if (isPaginationRow(node?.data)) {
    return undefined;
  }
  if (isTotalRow(node?.data)) {
    const rootNode = tryGetAgClientSideRowModel(api)?.getRootNode();
    const aggData: GenericTableRowCells | undefined = rootNode?.aggData;
    const cell = aggData?.[column.getColId()];
    const cellType = cell?.value?.type;
    if (
      cellType &&
      ["money", "decimal", "percent", "integer"].includes(cellType)
    ) {
      return cell;
    }
    const firstLeftColumn =
      first(api.getDisplayedLeftColumns()) ??
      first(api.getDisplayedCenterColumns());
    if (column === firstLeftColumn) {
      return {
        value: {
          type: "text",
          text: i18n.t("common:table.total"),
        },
      };
    }
  }
  const cell =
    (context as DataTableContext).valueGetter?.({
      api,
      data,
      node,
      column,
      context,
      ...params,
    }) ?? data?.cells[column.getColId()];
  if (cell) {
    return cell;
  }
  if (node?.childrenAfterFilter?.length) {
    return getCellForRowGroup({ node, column });
  }
  return undefined;
};

export const translateColumns = (
  { client_row_aggregation, columns }: GenericTableData,
  context: DataTableContext,
): ColOrGroupDef[] => {
  const {
    disablePinnedHeaderColumns,
    cellClassRules,
    getColumnDef,
    lockColumns,
  } = context;
  const firstInitialRowGroupColumn = getFirstInitialRowGroupColumn(columns);
  const translateColumnsRecursive = (
    columns: GenericTableColumn[],
    parentColumns: GenericTableColumn[] = [],
  ): ColOrGroupDef[] => {
    const rootColumn = parentColumns.length ? parentColumns[0] : undefined;

    // if one group, put rest in group
    const hasGroup = columns.some((c) => c.columns?.length);
    const isGroup = (column: GenericTableColumn) => !!column.columns?.length;
    if (hasGroup) {
      // collect all adjacent columns into same group
      let currentGroup: GenericTableColumn | undefined;
      columns = columns.reduce<GenericTableColumn[]>((acc, column) => {
        const newColumns = [...acc];
        if (isGroup(column)) {
          currentGroup = undefined;
          newColumns.push(column);
        } else {
          if (!currentGroup) {
            currentGroup = {
              id: crypto.randomUUID(),
              columns: [column],
              column_group_open_by_default: column.column_group_open_by_default,
              column_group_show: column.column_group_show,
            };
            newColumns.push(currentGroup);
          } else {
            currentGroup.columns!.push(column);
          }
        }
        return newColumns;
      }, []);
    }

    const hasColumnWithTitle = columns.some((column) => column.title);
    if (!hasColumnWithTitle && hasGroup) {
      return columns.flatMap((column) =>
        column.columns
          ? translateColumnsRecursive(column.columns, [
              ...parentColumns,
              column,
            ])
          : [],
      );
    }

    return columns.map((column): ColOrGroupDef => {
      const isHeader = column.is_header || rootColumn?.is_header;
      const isFirstInitialRowGroup =
        firstInitialRowGroupColumn?.id === column.id;
      const filterProps = getFilterPropsForColumn(context, column);
      // WARNING: Do not declare any inline functions here.
      // They will be re-created on every render causing a render loop.
      return {
        colId: column.id,
        field: column.id as never,
        // undefined will fall back to defaultColDef
        suppressMovable: isHeader || lockColumns || undefined,
        lockPosition: isHeader || lockColumns,
        lockPinned: isHeader || lockColumns,
        lockVisible: lockColumns,
        sortable: !lockColumns,
        initialSort: column.initial_sort,
        marryChildren: true,
        initialRowGroup: Boolean(
          client_row_aggregation && column.initial_row_group,
        ),
        openByDefault: column.column_group_open_by_default ?? undefined,
        columnGroupShow: column.column_group_show ?? undefined,
        groupId: column.id,
        initialHide: Boolean(
          column.initial_hide ||
            (client_row_aggregation &&
              column.initial_row_group &&
              isFirstInitialRowGroup),
        ),
        children: column.columns?.length
          ? translateColumnsRecursive(column.columns, [
              ...parentColumns,
              column,
            ])
          : undefined,
        headerName: column.title || "",
        headerComponent: DataTableHeaderComponent,
        headerGroupComponent: DataTableHeaderGroupComponent,
        headerComponentParams: { column, parentColumns },
        headerGroupComponentParams: { column, parentColumns },
        rowSpan,
        valueGetter,
        valueFormatter,
        cellRendererSelector,
        cellRendererParams: {
          innerRenderer: DataTableHeaderCellRenderer,
          suppressCount: true,
        },
        defaultAggFunc:
          client_row_aggregation &&
          column.aggregatable &&
          column.only_apply_aggregation_by_default
            ? "dataTableCellSumAggFunc"
            : undefined,
        aggFunc:
          client_row_aggregation &&
          column.aggregatable &&
          !column.only_apply_aggregation_by_default
            ? "dataTableCellSumAggFunc"
            : undefined,
        allowedAggFuncs: client_row_aggregation
          ? ["dataTableCellSumAggFunc"]
          : undefined,
        keyCreator,
        pinned:
          !disablePinnedHeaderColumns &&
          (column.is_header ||
            rootColumn?.is_header ||
            column.is_pinned ||
            rootColumn?.is_pinned)
            ? "left"
            : undefined,
        rowDrag: column.row_drag,
        enablePivot: column.enable_pivot ?? true,
        enableRowGroup: column.enable_row_group,
        enableValue: Boolean(column.aggregatable_type && column.aggregatable),
        suppressColumnsToolPanel:
          !column.enable_pivot &&
          !column.aggregatable &&
          !column.enable_row_group,
        cellStyle,
        cellClass,
        cellClassRules,
        headerClass: dataTableHeaderClass,
        ...filterProps,
        ...getColumnDef?.({ column, rootColumn }),
      };
    });
  };
  return translateColumnsRecursive(columns);
};

const ACTIONS_COL_DEF: ColDef = {
  colId: COLUMN_ID_ACTIONS,
  flex: 0,
  lockPinned: true,
  lockPosition: "right",
  pinned: "right",
  suppressMovable: true,
  sortable: false,
  resizable: false,
  suppressSizeToFit: true,
  suppressHeaderMenuButton: true,
  headerComponentParams: {},
  minWidth: 76,
  cellStyle: (props) => ({
    justifyContent: "flex-end",
    display: props.node.rowPinned ? "none" : "flex",
  }),
  cellRenderer: (props: ICellRendererParams) => {
    const { renderRowActions }: DataTableContext = props.context;
    const rowActions = renderRowActions?.({
      row: props.node,
    });
    return rowActions?.actions ? (
      <DataTableRowActions rowActions={rowActions} />
    ) : null;
  },
};

const getFilterPropsForColumn = (
  context: DataTableContext,
  column: GenericTableColumn,
): IFilterDef =>
  match(
    column.cell_value_type ??
      context.columnInferredData.get(column.id)?.valueType,
  )
    .with("boolean", () => ({
      filter: "agSetColumnFilter",
      filterParams: {
        values: [true, false],
        valueFormatter: (params) =>
          params.value ? i18n.t("common:yes") : i18n.t("common:no"),
        keyCreator: (params) => (params.value as boolean).toString(),
      } as ISetFilterParams<GenericTableRow, boolean>,
    }))
    .when(ifHasSetFilterValues(context, column), () => ({
      filter: "agSetColumnFilter",
      filterParams: {
        values:
          column.set_filter_values?.filter(
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            (filterValue) => filterValue?.id && filterValue?.value,
          ) ?? //
          context.columnInferredData.get(column.id)?.setFilterValues ??
          [],
        valueFormatter: ((params) =>
          params.value?.value ?? "") as ValueFormatterFunc<
          GenericTableRow,
          GenericTableColumnSetFilterValue
        >,
        keyCreator: ((params) => params.value?.id ?? "") as (
          params: KeyCreatorParams<
            GenericTableRow,
            GenericTableColumnSetFilterValue
          >,
        ) => string,
        comparator: (a, b) => (a?.value ?? "").localeCompare(b?.value || ""),
        getValue: (node: IRowNode<GenericTableRow>) => {
          const cell = node.data?.cells[column.id];
          if (!cell) {
            return null;
          }
          return (
            cell.filter_values?.map((value) => ({ id: value, value })) ?? {
              id: valueForCellFilter(cell),
              value: valueForCellFilter(cell),
            }
          );
        },
      } as ISetFilterParams<GenericTableRow, GenericTableColumnSetFilterValue>,
    }))
    .with("date", () => ({ filter: "agDateColumnFilter" }))
    .with("datetime", () => ({ filter: "agDateColumnFilter" }))
    .with("decimal", () => ({ filter: "agNumberColumnFilter" }))
    .with("integer", () => ({ filter: "agNumberColumnFilter" }))
    .with("money", () => ({ filter: "agNumberColumnFilter" }))
    .with("text", () => ({ filter: "agTextColumnFilter" }))
    .with("percent", () => ({ filter: "agNumberColumnFilter" }))
    .with("formula", () => ({ filter: undefined }))
    .with(P.nullish, () => ({}))
    .exhaustive();

const ifHasSetFilterValues =
  (context: DataTableContext, column: GenericTableColumn) => () =>
    Boolean(
      column.set_filter_values?.length ||
        context.columnInferredData.get(column.id)?.setFilterValues?.length,
    );
